// @ts-check
/**
 * @typedef {import('./types').AuthHealthResponse} AuthHealthResponse
 * @typedef {import('./types').AuthCallbackResponse} AuthCallbackResponse
 * @typedef {import('./types').AuthMeResponse} AuthMeResponse
 * @typedef {import('./types').AuthPhoneLoginResponse} AuthPhoneLoginResponse
 */

// Authentication API endpoints
import { API_BASE_URL } from '../../config/api';
import { mark } from '../../utils/bootTiming';

/**
 * Fetch with AbortController timeout to prevent hanging when backend is down.
 * @param {string} url
 * @param {RequestInit} [options]
 * @param {number} [timeoutMs=8000]
 * @returns {Promise<Response>}
 */
function fetchWithTimeout(url, options = {}, timeoutMs = 8000) {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  return fetch(url, { ...options, signal: controller.signal })
    .then(res => { clearTimeout(timeoutId); return res; })
    .catch(err => { clearTimeout(timeoutId); throw err; });
}

/**
 * Exchange auth code for session (creates HttpOnly cookie)
 * @returns {Promise<AuthCallbackResponse>}
 */
export const exchangeAuthCode = async ({ code, redirectUri, codeVerifier, state }) => {
  const response = await fetchWithTimeout(`${API_BASE_URL}/auth/callback`, {
    method: 'POST',
    credentials: 'include', // Receive HttpOnly cookie
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      code,
      redirect_uri: redirectUri,
      code_verifier: codeVerifier,
      state
    })
  }, 10000);

  if (!response.ok) {
    // Expose status + body snippet so PostHog auth-failure breakdown is
    // actionable instead of an opaque "Error: 7" bar (the PostHog default
    // Error.name is uselessly generic; the status code is the real taxonomy).
    const bodySnippet = await response.text().catch(() => null);
    const err = new Error(`Token exchange failed: HTTP ${response.status}`);
    err.status = response.status;
    err.body = bodySnippet ? bodySnippet.slice(0, 200) : null;
    throw err;
  }

  return response.json();
};

/**
 * Phone-only login (the claim page): a new number gets an account on 123456;
 * an existing one logs in, with `password` only when it has its own. Sets the
 * sid cookie like the Authing callback. Plain fetch, not apiRequest — its
 * refusals must not trip apiRequest's "401 = session expired" redirect.
 * Throws an Error whose `code` is the backend's reason: bad_phone,
 * need_password, wrong_password, staff_account, login_failed (or network).
 * `code` (the claim page's) lets the 扫码漏斗 tie the outcome to that follower.
 * @param {{ phone: string, password?: string, code?: string }} body
 * @returns {Promise<AuthPhoneLoginResponse>}
 */
export const loginWithPhone = async ({ phone, password, code }) => {
  let response;
  try {
    response = await fetchWithTimeout(`${API_BASE_URL}/auth/phone-login`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ phone, ...(password ? { password } : {}), ...(code ? { code } : {}) }),
    }, 20000);
  } catch (e) {
    const err = new Error('network');
    // @ts-ignore — reason code for the UI
    err.code = 'network';
    throw err;
  }
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const err = new Error(data.error || `HTTP ${response.status}`);
    // @ts-ignore — reason code for the UI
    err.code = data.error || 'login_failed';
    throw err;
  }
  return data;
};

/**
 * Get current user data
 * @returns {Promise<AuthMeResponse>}
 */
export const getCurrentUser = async () => {
  const response = await fetchWithTimeout(`${API_BASE_URL}/auth/me`, {
    credentials: 'include' // Send sid cookie
  });

  if (!response.ok) {
    throw new Error('Failed to fetch user data');
  }

  return response.json();
};

/**
 * Variant of getCurrentUser that surfaces the HTTP status instead of throwing
 * on non-2xx. Used by the AuthContext fast-path (skip-checkSession) so the
 * caller can distinguish 401 (session expired → clear state + redirect) from
 * network / 5xx errors (preserve cached user for graceful degradation).
 *
 * Returns { status, data } on any HTTP response; throws only on true network
 * errors (timeout, DNS, ECONNREFUSED).
 *
 * @returns {Promise<{status: number, data: (AuthMeResponse|null)}>}
 */
export const getCurrentUserWithStatus = async () => {
  const response = await fetchWithTimeout(`${API_BASE_URL}/auth/me`, {
    credentials: 'include'
  });
  const data = response.ok ? await response.json() : null;
  return { status: response.status, data };
};

/**
 * Check if session exists
 * @returns {Promise<AuthHealthResponse>}
 */
export const checkSession = async () => {
  // Network errors (timeout, ECONNREFUSED, etc.) throw naturally —
  // AuthContext catches them and preserves cached user during outages.
  // TTFB instrumentation: captures request duration separately from response
  // parsing so we can distinguish "backend down" (throws, no ttfb) from
  // "backend slow" (ttfb = N seconds) in the boot-timeline data.
  const hasPerf = typeof performance !== 'undefined';
  const t0 = hasPerf ? performance.now() : 0;
  let response;
  try {
    response = await fetchWithTimeout(`${API_BASE_URL}/auth/health`, {
      credentials: 'include',
      cache: 'no-store',
      headers: {
        'Cache-Control': 'no-cache',
        'Pragma': 'no-cache'
      }
    });
  } finally {
    if (hasPerf) {
      mark('auth_health_ttfb', { ms: Math.round(performance.now() - t0) });
    }
  }

  if (!response.ok) {
    return { hasSession: false, configured: false };
  }

  return response.json();
};

/**
 * Single-roundtrip cold-boot composition. Replaces getCurrentUserWithStatus +
 * the dashboard fetch that would otherwise fire separately on mount.
 *
 * Returns {status, data} where data is { user, dashboard, hasSession, fetchedAt }
 * on 200, or null on non-2xx. Same status/data contract as
 * getCurrentUserWithStatus so the AuthContext error handling is unchanged.
 *
 * @param {string} [studentId] - Optional; defaults to caller's own ID backend-side.
 * @returns {Promise<{status: number, data: any}>}
 */
export const fetchBootstrap = async (studentId) => {
  const qs = studentId ? `?studentId=${encodeURIComponent(studentId)}` : '';
  const response = await fetchWithTimeout(`${API_BASE_URL}/bootstrap${qs}`, {
    credentials: 'include'
  });
  const data = response.ok ? await response.json() : null;
  return { status: response.status, data };
};

// Note: Logout is now handled via direct navigation to /api/auth/logout
// which clears the cookie and redirects. No API call needed.
